import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FilterTypes, ILiveLocationTrackerSlice } from '../constants/LiveLocationTrackerInterfaces';
import { Option } from 'src/pages/CaseDetails/interfaces/EmiSchedule.type';
import { getServerTime } from 'src/utils/DateHelper';

const initialState: ILiveLocationTrackerSlice = {
  filterTypes: {
    filters: [],
    loadOnMount: false
  },
  filterData: {
    [FilterTypes.AGENCY]: {
      data: [],
      selectedValue: null
    },
    [FilterTypes.TEAM_LEAD]: {
      data: [],
      selectedValue: null
    }
  },
  agentLocations: null,
  showAllInfoWindows: false,
  loadingFilters: true,
  showBlurMap: false,
  lastUpdatedAt: new Date(getServerTime()).toString(),
  selectedAgent: null,
  mapLocations: [],
  agentLocationHistory: {
    data: [],
    loading: false
  },
  agentAllocations: {
    data: null,
    allocationsList: [],
    loading: false
  },
  selectedFeedback: {
    lanNo: '',
    interactionId: '',
    feedbackLocation: null,
    caseLocation: null,
    isSuspicious: false
  },
  isAccordionExpanded: true,
  disabledFilters: {},
  agentLocationsLoading: false,
  caseFilters: {
    filters: [],
    loading: false
  }
};

const liveLocationTrackerSlice = createSlice({
  name: 'liveLocationTracker',
  initialState,
  reducers: {
    setFilterTypes: (state, action) => {
      const { data } = action.payload;
      state.filterTypes = data;
    },
    setFilterData: (
      state,
      action: PayloadAction<{ filterType: FilterTypes; data: Array<Option> }>
    ) => {
      const { data, filterType } = action.payload;
      state.filterData[filterType].data = data;
    },
    setAgentLocationsLoading: (state, action) => {
      state.agentLocationsLoading = action.payload;
    },
    setAgentLocations: (state, action) => {
      const { data } = action.payload;
      state.agentLocations = data;
      state.lastUpdatedAt = new Date(getServerTime()).toString();
    },
    setFilterValue: (state, action) => {
      const { filterType, value } = action.payload;
      state.filterData[filterType].selectedValue = value;
    },
    setShowAllInfoWindows: (state, action) => {
      state.showAllInfoWindows = action.payload;
    },
    setLoadingFilters: (state, action) => {
      state.loadingFilters = action.payload;
    },
    setShowBlurMap: (state, action) => {
      state.showBlurMap = action.payload;
    },
    setSelectedAgent: (state, action) => {
      state.selectedAgent = action.payload;
    },
    setMapLocations: (state, action) => {
      const { data = [], disabledFilters = {} } = action.payload || {};
      state.mapLocations = data;
      state.disabledFilters = disabledFilters;
    },
    setAgentLocationHistory: (state, action) => {
      state.agentLocationHistory = {
        ...state.agentLocationHistory,
        ...action.payload
      };
    },
    setAgentAllocationsLoading: (state, action) => {
      state.agentAllocations.loading = action.payload;
    },
    setAgentAllocations: (state, action) => {
      state.agentAllocations = {
        ...state.agentAllocations,
        ...action.payload
      };
      state.lastUpdatedAt = new Date(getServerTime()).toString();
    },
    setSelectedFeedback: (state, action) => {
      state.selectedFeedback = action.payload;
    },
    setIsAccordionExpanded: (state, action) => {
      state.isAccordionExpanded = action.payload;
    },
    setCaseFilters: (state, action) => {
      if (action.payload) {
        state.caseFilters.filters = action.payload;
      }
    },
    setCaseFiltersLoading: (state, action) => {
      state.caseFilters.loading = action.payload;
    },
    resetMapData: () => initialState
  }
});

export const {
  setFilterTypes,
  setFilterData,
  setAgentLocations,
  setFilterValue,
  setShowAllInfoWindows,
  setLoadingFilters,
  setShowBlurMap,
  setAgentLocationHistory,
  setAgentAllocations,
  setSelectedAgent,
  setMapLocations,
  setSelectedFeedback,
  setIsAccordionExpanded,
  setAgentLocationsLoading,
  resetMapData,
  setCaseFilters,
  setCaseFiltersLoading
} = liveLocationTrackerSlice.actions;

export default liveLocationTrackerSlice.reducer;
